import { mapPaginatedResponse, type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  CreateTemplateConfiguration,
  PaginatedResponse,
  PharosApiResponse,
  PharosPaginatedResponse,
  TemplateConfiguration,
  TemplateConfigurationStatus,
  TemplateConfigurationWithResolvedRefs,
  UpdateTemplateConfiguration,
  UpdateTemplateConfigurationStatus,
} from '@klareospace/pharos-lib-types';

import type { SearchTemplateConfigurationsParams } from './template-configuration.keys';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TemplateConfigurationListResult = PaginatedResponse<TemplateConfigurationWithResolvedRefs>;

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createTemplateConfigurationApi(client: ApiClient): {
  createTemplateConfiguration: (body: CreateTemplateConfiguration) => Promise<TemplateConfiguration>;
  getTemplateConfiguration: (id: string) => Promise<TemplateConfiguration>;
  searchTemplateConfigurations: (
    params: SearchTemplateConfigurationsParams,
  ) => Promise<TemplateConfigurationListResult>;
  updateTemplateConfiguration: (id: string, body: UpdateTemplateConfiguration) => Promise<TemplateConfiguration>;
  updateTemplateConfigurationStatus: (
    id: string,
    body: UpdateTemplateConfigurationStatus,
  ) => Promise<TemplateConfiguration>;
} {
  return {
    createTemplateConfiguration: async (body: CreateTemplateConfiguration): Promise<TemplateConfiguration> => {
      const response = await client.execute<PharosApiResponse<TemplateConfiguration>>(() =>
        client.instance.post('/template-configurations', body),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    getTemplateConfiguration: async (id: string): Promise<TemplateConfiguration> => {
      const response = await client.execute<PharosApiResponse<TemplateConfiguration>>(() =>
        client.instance.get(`/template-configurations/${id}`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    searchTemplateConfigurations: async (
      params: SearchTemplateConfigurationsParams,
    ): Promise<TemplateConfigurationListResult> => {
      const query: Record<string, number> = {
        limit: params.pageSize,
        page: params.page,
      };
      const body: { search?: string; statuses: TemplateConfigurationStatus[] } = {
        statuses: params.statuses,
      };
      const trimmedSearch = params.search?.trim();
      if (trimmedSearch) {
        body.search = trimmedSearch;
      }
      const response = await client.execute<
        PharosApiResponse<PharosPaginatedResponse<TemplateConfigurationWithResolvedRefs>>
      >(() => client.instance.post('/template-configurations/search', body, { params: query }));
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return mapPaginatedResponse(response.data);
    },

    updateTemplateConfiguration: async (
      id: string,
      body: UpdateTemplateConfiguration,
    ): Promise<TemplateConfiguration> => {
      const response = await client.execute<PharosApiResponse<TemplateConfiguration>>(() =>
        client.instance.patch(`/template-configurations/${id}`, body),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    updateTemplateConfigurationStatus: async (
      id: string,
      body: UpdateTemplateConfigurationStatus,
    ): Promise<TemplateConfiguration> => {
      const response = await client.execute<PharosApiResponse<TemplateConfiguration>>(() =>
        client.instance.patch(`/template-configurations/${id}/status`, body),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },
  };
}
